import { orderBy } from 'lodash';
import type { Poll, Question } from 'schema/poll';
import type { AnswerState, ICState } from 'schema/state';
import { calcAwardAmount, isAnswersCorrect } from 'shared/src/utils';
import { groupQuestionsByDeck } from '../external/pollPreStartTransformer';
import { InterpreterIntl } from '../machine/interpreter';
import { shuffle } from '../polyfills';
import {
  getQuestionGroupId,
  selectDisplayedOrAllGroupQuestions,
  selectFinal,
  selectGroup,
  selectGroupQuestions,
  selectGroupQuestionsIds,
  selectQuestion,
  selectQuestionDeck,
} from '../selects';
import {
  findLastInStack,
  getGroupFirstQuestionInStackIndex,
  getGroupQuestionsInStateIds,
  getLogicConditionContext,
  isOtherAnswer,
  isQuestionRulesApply,
  isSystemAnswer,
  pushToStack,
  questionsAfterRules,
} from '../utils';
import { createDefaultAnswerState } from './creator';
import { validateAnswers, validateUnanswerable } from './validate';

export function reduceQuestion({
  poll,
  prevState,
  questionId,
}: {
  poll: Poll;
  prevState: ICState;
  questionId: string;
}): ICState {
  const { defaultAnswerState, backStackItem } = createDefaultAnswerState(poll, prevState, questionId);
  const backAnswerStack = prevState.backAnswerStack.filter(it => it !== backStackItem);

  const answersStack = pushToStack(prevState.answersStack, defaultAnswerState);

  return {
    ...prevState,
    answersStack,
    questionId,
    groupId: null,
    backAnswerStack,
  };
}

export function reduceQuestionGroup({
  poll,
  prevState,
  groupId,
  recalculateVisibleQuestions,
}: {
  poll: Poll;
  prevState: ICState;
  groupId: string;
  recalculateVisibleQuestions?: boolean;
}): ICState {
  const group = selectGroup(poll, groupId);
  const allGroupQuestions = selectGroupQuestions(poll, groupId);
  const allGroupQuestionIds = selectGroupQuestionsIds(poll, groupId);

  const context = getLogicConditionContext(
    /*
      backAnswerStack нужен для того, чтобы при возвращении отобразить
      вопросы, чьи логические условия настроены на вопросы этой же группы
    */
    [
      ...prevState.answersStack,
      ...prevState.backAnswerStack
        .filter(({ id }) => allGroupQuestionIds.includes(id))
        .map(({ id }) => createDefaultAnswerState(poll, prevState, id).defaultAnswerState),
    ],
    poll.questions,
    prevState.externalLogicRules,
  );

  let visibleGroupQuestions = questionsAfterRules(
    recalculateVisibleQuestions
      ? allGroupQuestions
      : selectDisplayedOrAllGroupQuestions(poll, groupId, prevState.answersStack),
    context,
  );

  let nextAnswersStack = [...prevState.answersStack];
  let nextBackAnswerStack = [...prevState.backAnswerStack];

  const createAnswerState = (questionId: string) => {
    return createDefaultAnswerState(poll, { ...prevState, answersStack: nextAnswersStack }, questionId);
  };

  const createAnswerStateInStacks = (questionId: string) => {
    const { defaultAnswerState, backStackItem } = createAnswerState(questionId);
    nextBackAnswerStack = nextBackAnswerStack.filter(it => it !== backStackItem);
    nextAnswersStack.push(defaultAnswerState);
  };

  const updateAnswerStateInStack = (questionId: string) => {
    const {
      defaultAnswerState: { answersIds: nextAnswersIds, answersOrder: nextAnswersOrder },
    } = createAnswerState(questionId);
    const nextStackAnswerStateIndex = nextAnswersStack.findIndex(it => it.id === questionId);
    const prevAnswerState = nextAnswersStack[nextStackAnswerStateIndex];
    const question = poll.questions.find(q => q.id === questionId);

    if (!prevAnswerState || !question) {
      return;
    }

    const { answeredIds: prevAnsweredIds, answeredOthers: prevAnsweredOthers } = prevAnswerState;

    const answers = 'answers' in question.data ? question.data.answers : [];
    const questionHasOnlySystemAnswers = answers.every(isSystemAnswer);
    const answeredIds = prevAnsweredIds?.filter(idOrValue => {
      const isValue = answers.every(a => a.id !== idOrValue);
      return nextAnswersIds?.includes(idOrValue) || (questionHasOnlySystemAnswers && isValue);
    });

    const questionHasOtherAnswer = answers.some(isOtherAnswer);
    const otherAnswerIsShown = !!nextAnswersIds?.some(id => {
      const answer = answers.find(a => a.id === id);
      return answer && isOtherAnswer(answer);
    });
    const answeredOthers = questionHasOtherAnswer && !otherAnswerIsShown ? undefined : prevAnsweredOthers;

    nextAnswersStack[nextStackAnswerStateIndex] = {
      ...prevAnswerState,
      answersIds: nextAnswersIds,
      answersOrder: nextAnswersOrder,
      answeredIds,
      answeredOthers,
    };
  };

  if (recalculateVisibleQuestions) {
    nextAnswersStack = removeHiddenQuestionStateFromStack(visibleGroupQuestions, allGroupQuestionIds, nextAnswersStack);
  }

  if (group.data.hasSequentialDisplay) {
    const [firstVisibleQuestion] = visibleGroupQuestions;
    if (firstVisibleQuestion) {
      createAnswerStateInStacks(firstVisibleQuestion.id);
    }
  } else {
    const currentGroupQuestionsInStateIds = getGroupQuestionsInStateIds(nextAnswersStack, poll, groupId);

    const questionLimit =
      group.data.questionLimit && group.data.questionLimit > 0
        ? group.data.questionLimit
        : visibleGroupQuestions.length;

    visibleGroupQuestions = limitGroupQuestions({
      visibleGroupQuestions,
      groupQuestionsInStateIds: currentGroupQuestionsInStateIds,
      randomizeGroupExcludedIds: group.data.randomizeGroupExcludedIds ?? [],
      randomizeGroup: !!group.data.randomizeGroup,
      backAnswerStack: nextBackAnswerStack,
      questionLimit,
    });

    visibleGroupQuestions = limitDeckQuestions({
      visibleGroupQuestions,
      groupQuestionsInStateIds: currentGroupQuestionsInStateIds,
      backAnswerStack: nextBackAnswerStack,
      poll,
    });

    visibleGroupQuestions.forEach(question => {
      const questionAlreadyInState = currentGroupQuestionsInStateIds.includes(question.id);
      const shouldCreateState = recalculateVisibleQuestions ? !questionAlreadyInState : true;
      const shouldUpdateState = !!recalculateVisibleQuestions && questionAlreadyInState;

      if (shouldCreateState) {
        createAnswerStateInStacks(question.id);
      }

      if (shouldUpdateState) {
        updateAnswerStateInStack(question.id);
      }
    });
  }

  if (recalculateVisibleQuestions) {
    nextAnswersStack = orderBy(nextAnswersStack, it => visibleGroupQuestions.findIndex(q => q.id === it.id));
  }

  return {
    ...prevState,
    groupId,
    questionId: null,
    answersStack: nextAnswersStack,
    backAnswerStack: nextBackAnswerStack,
  };
}

function removeHiddenQuestionStateFromStack(
  visibleGroupQuestions: Question[],
  allGroupQuestionIds: string[],
  answersStack: ICState['answersStack'],
): ICState['answersStack'] {
  const firstGroupAnswerStateIndex = getGroupFirstQuestionInStackIndex(answersStack, allGroupQuestionIds);

  // удаление состояний вопросов, которые теперь не отображаются
  if (firstGroupAnswerStateIndex !== undefined) {
    return [
      ...answersStack.slice(0, firstGroupAnswerStateIndex),
      ...answersStack.slice(firstGroupAnswerStateIndex).filter(it => visibleGroupQuestions.some(q => q.id === it.id)),
    ];
  }

  return answersStack;
}

function limitGroupQuestions({
  randomizeGroupExcludedIds,
  groupQuestionsInStateIds,
  visibleGroupQuestions,
  backAnswerStack,
  randomizeGroup,
  questionLimit,
}: {
  backAnswerStack: ICState['backAnswerStack'];
  randomizeGroupExcludedIds: string[];
  groupQuestionsInStateIds: string[];
  visibleGroupQuestions: Question[];
  randomizeGroup: boolean;
  questionLimit: number;
}): Question[] {
  const backStackIndexMap: { [id: string]: number } = backAnswerStack.reduce(
    (result, item, index) => ({ ...result, [item.id]: index }),
    {},
  );

  const groupQuestionsInStateIdsIndexMap: { [id: string]: number } = groupQuestionsInStateIds.reduce(
    (result, id, index) => ({ ...result, [id]: index }),
    {},
  );

  let questionsAndDecks = groupQuestionsByDeck(visibleGroupQuestions);
  questionsAndDecks = randomizeGroup ? shuffle(questionsAndDecks) : questionsAndDecks;

  const getDeckOrQuestionIndexInMap = (
    deckOrQuestion: ReturnType<typeof groupQuestionsByDeck>[number],
    indexMap: { [id: string]: number },
  ) => {
    const getIndexInMap = (questionId: string) => indexMap[questionId] ?? -1;

    if (deckOrQuestion.type === 'deck') {
      return Math.max(...deckOrQuestion.items.map(question => getIndexInMap(question.id)));
    }

    return getIndexInMap(deckOrQuestion.items[0].id);
  };

  const limitedQuestionsIds = new Set<string>();

  /** MACP-1755 При включенной рандомизации дочерних элементов органичение показа будет учитывать только элементы, участвующие в рандомизации */
  if (randomizeGroup && randomizeGroupExcludedIds.length) {
    const excludedIdsSet = new Set(randomizeGroupExcludedIds);

    questionsAndDecks = questionsAndDecks.filter(it => {
      if (excludedIdsSet.has(it.type === 'deck' ? it.id : it.items[0].id)) {
        it.items.forEach(q => limitedQuestionsIds.add(q.id));
        return false;
      }
      return true;
    });
  }

  questionsAndDecks
    /** Сортируем по backAnswerStack, чтобы при questionLimit показать, ранее показанные, вопросы */
    .sort(
      (a, b) => getDeckOrQuestionIndexInMap(b, backStackIndexMap) - getDeckOrQuestionIndexInMap(a, backStackIndexMap),
    )
    /** Сортируем по groupQuestionsInStateIds, чтобы при questionLimit показать уже показанные вопросы */
    .sort(
      (a, b) =>
        getDeckOrQuestionIndexInMap(b, groupQuestionsInStateIdsIndexMap) -
        getDeckOrQuestionIndexInMap(a, groupQuestionsInStateIdsIndexMap),
    )
    .slice(0, questionLimit)
    .flatMap(deckOrQuestion =>
      deckOrQuestion.type === 'deck' ? deckOrQuestion.items.map(it => it.id) : deckOrQuestion.items[0].id,
    )
    .forEach(questionId => limitedQuestionsIds.add(questionId));

  return visibleGroupQuestions.filter(q => limitedQuestionsIds.has(q.id));
}

function limitDeckQuestions({
  visibleGroupQuestions,
  groupQuestionsInStateIds,
  backAnswerStack,
  poll,
}: {
  visibleGroupQuestions: Question[];
  backAnswerStack: ICState['backAnswerStack'];
  groupQuestionsInStateIds: string[];
  poll: Poll;
}): Question[] {
  const backStackIndexMap: { [id: string]: number } = backAnswerStack.reduce(
    (result, item, index) => ({ ...result, [item.id]: index }),
    {},
  );

  const groupQuestionsInStateIdsIndexMap: { [id: string]: number } = groupQuestionsInStateIds.reduce(
    (result, id, index) => ({ ...result, [id]: index }),
    {},
  );

  const groupedQuestions = groupQuestionsByDeck(visibleGroupQuestions);

  const plainQuestions = groupedQuestions.filter(
    (item): item is Extract<ReturnType<typeof groupQuestionsByDeck>[number], { type: 'question' }> =>
      item.type === 'question',
  );
  const deckQuestions = groupedQuestions.filter(
    (item): item is Extract<ReturnType<typeof groupQuestionsByDeck>[number], { type: 'deck' }> => item.type === 'deck',
  );

  const limitedQuestionsIds = new Set<string>(plainQuestions.map(q => q.items[0].id));

  for (const { id: deckId, items } of deckQuestions) {
    const deck = selectQuestionDeck(poll, deckId);
    let questions = deck.data.randomizeDeck ? shuffle(items) : items;

    const questionLimit =
      deck.data.questionLimit && deck.data.questionLimit > 0 ? deck.data.questionLimit : visibleGroupQuestions.length;

    /** MACP-1755 При включенной рандомизации дочерних элементов органичение показа будет учитывать только элементы, участвующие в рандомизации */
    if (deck.data.randomizeDeck && deck.data.randomizeDeckExcludedIds?.length) {
      const excludedIdsSet = new Set(deck.data.randomizeDeckExcludedIds);

      questions = questions.filter(q => {
        if (excludedIdsSet.has(q.id)) {
          limitedQuestionsIds.add(q.id);
          return false;
        }
        return true;
      });
    }

    questions
      /** Сортируем по backAnswerStack, чтобы при questionLimit показать, ранее показанные, вопросы */
      .sort((a, b) => (backStackIndexMap[b.id] ?? -1) - (backStackIndexMap[a.id] ?? -1))
      /** Сортируем по groupQuestionsInStateIds, чтобы при questionLimit показать уже показанные вопросы */
      .sort((a, b) => (groupQuestionsInStateIdsIndexMap[b.id] ?? -1) - (groupQuestionsInStateIdsIndexMap[a.id] ?? -1))
      .slice(0, questionLimit)
      .forEach(question => limitedQuestionsIds.add(question.id));
  }

  return visibleGroupQuestions.filter(q => limitedQuestionsIds.has(q.id));
}

export function reduceFirst(data: Poll, prevState: ICState, index: number = 0): ICState {
  const question = selectQuestion(data, data.firstId);
  if (question && isQuestionRulesApply(data, question.id, prevState.answersStack, prevState.externalLogicRules)) {
    const state = reduceQuestion({ poll: data, prevState, questionId: question.id });
    if (state.answersStack.length) {
      return state;
    }
  }

  const group = selectGroup(data, data.firstId);
  if (group) {
    const state = reduceQuestionGroup({ poll: data, prevState, groupId: group.id });
    if (state.answersStack.length) {
      return state;
    }
  }

  const final = selectFinal(data, data.firstId);
  if (final) return { ...prevState, questionId: null, groupId: null, finalId: final.id };

  if (data.env !== 'prod') {
    console.error(`A page with this firstId was not found: ${data.firstId}`);
  }

  let firstId = '';
  if (index < data.questions.length) {
    const question = data.questions[index];
    const groupId = question ? getQuestionGroupId(question, data.questionDecks) : undefined;
    firstId = groupId || question!.id;
  } else if (data.finals[0]?.id) {
    firstId = data.finals[0]?.id;
  } else {
    throw new Error('First screen not found, empty page cannot be displayed');
  }

  return reduceFirst({ ...data, firstId }, prevState, index + 1);
}

export function markUnanswerableValidity(prevState: ICState, question: Question, intl: InterpreterIntl) {
  const answerState = findLastInStack(prevState.answersStack, question.id);

  const answersStack = pushToStack(prevState.answersStack, {
    ...answerState,
    errorMessage: validateUnanswerable(question, answerState, intl),
  });
  return { ...prevState, answersStack };
}

export function markAnswerValidity(
  prevState: ICState,
  question: Question,
  relevantQuestions: Question[],
  intl: InterpreterIntl,
) {
  const answerState = findLastInStack(prevState.answersStack, question.id);

  const answersStack = pushToStack(prevState.answersStack, {
    ...answerState,
    errorMessage: validateAnswers(question, relevantQuestions, prevState.answersStack, intl),
  });
  return { ...prevState, answersStack };
}

export function markAnswerAutoCommited(prevState: ICState, question: Question) {
  const found = findLastInStack(prevState.answersStack, question.id);

  const answersStack = pushToStack(prevState.answersStack, { ...found, isAutoCommit: true });
  return { ...prevState, answersStack };
}

export function markAnswerTracked(prevState: ICState, question: Question) {
  const found = findLastInStack(prevState.answersStack, question.id);

  const answersStack = pushToStack(prevState.answersStack, { ...found, isStored: true });
  return { ...prevState, answersStack };
}

export function markAnswerAnswered(prevState: ICState, question: Question) {
  const found = findLastInStack(prevState.answersStack, question.id);
  const { answeredIds = [], answeredOthers } = found;
  const isCorrect = isAnswersCorrect(question, answeredIds, answeredOthers);

  const now = new Date().getTime();
  const elapsedTime = now - found.createdAt!; //@Check-Undefined
  const awardAmount = calcAwardAmount(question, answeredIds, isCorrect);
  const isAnswerSelected = found.answeredIds?.length || found.answeredOthers;
  const answersStack = pushToStack(prevState.answersStack, {
    ...found,
    isCorrect,
    elapsedTime,
    awardAmount,
    errorMessage: undefined,
    isAnswered: true,
    isSkipped: !found.isWithoutAnswers && !isAnswerSelected,
  });

  const state: ICState = { ...prevState, answersStack };
  return state;
}

export function markAnswerSkipped(prevState: ICState, question: Question) {
  const found = findLastInStack(prevState.answersStack, question.id);
  const { answeredIds, answeredOthers, awardAmount, errorMessage, ...stateWithoutAnswers } = found;

  const now = new Date().getTime();
  const elapsedTime = now - found.createdAt!; //@Check-Undefined

  const answersStack = pushToStack(prevState.answersStack, {
    ...stateWithoutAnswers,
    elapsedTime,
    awardAmount: 0,
    errorMessage: undefined,
    isSkipped: true,
    isAnswered: true,
    isCorrect: false,
    isAutoCommit: false,
  });

  const state: ICState = { ...prevState, answersStack };
  return state;
}

export function reduceSequentialQuestionGroup({
  poll,
  prevState,
  questionId,
}: {
  poll: Poll;
  prevState: ICState;
  questionId: string;
}) {
  const lastGroupQuestion = prevState.answersStack.at(-1);

  const answersStack = [...prevState.answersStack];

  const { defaultAnswerState, backStackItem } = createDefaultAnswerState(poll, prevState, questionId);
  const backAnswerStack = prevState.backAnswerStack.filter(it => it !== backStackItem);

  answersStack.push({
    ...defaultAnswerState,
    isTimeExpired:
      lastGroupQuestion?.isTimeExpired !== undefined ? 'changedSinceExpired' : defaultAnswerState.isTimeExpired,
  });
  return { ...prevState, backAnswerStack, answersStack };
}
export function markQuestionExpired(state: ICState, question: Poll['questions'][0]): ICState {
  const stack = state.answersStack;
  const answerState = findLastInStack(state.answersStack, question.id);

  return {
    ...state,
    answersStack: pushToStack<AnswerState>(stack, {
      ...answerState,
      isTimeExpired: answerState.isTimeExpired ?? 'unchangedSinceExpired',
    }),
  };
}
